import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FontFamily } from "../theme/fontFamily";
import { fontWeight } from "../theme/fontWeight";
import { fontSize } from "../theme/fontSize";
import { ThemeColor } from "../theme/color";

/**
 * Shared shell for the payout modals.
 *
 * They used to each set `height: 60vh; overflow: scroll`, which fixed the box
 * to 60% of the screen whatever it held: the title scrolled away with the
 * content and the submit button sat below the fold, so the form looked like it
 * had no way to save. A max-height with a pinned header and footer keeps the
 * action visible and only scrolls the fields.
 */
export const payoutModalStyle = (width = 720) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "90%", md: width },
  maxWidth: "95vw",
  maxHeight: "90vh",
  bgcolor: "#FFFFFF",
  borderRadius: "16px",
  boxShadow: "0 20px 50px rgba(0, 0, 0, 0.15)",
  border: "none",
  outline: "none",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
});

/** The scrolling middle. Everything that is not the header or the footer. */
export const payoutModalBodySx = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  px: { xs: 2.5, md: 3.5 },
  py: 3,
  "&::-webkit-scrollbar": { width: "8px" },
  "&::-webkit-scrollbar-track": { background: "#F1F5F9" },
  "&::-webkit-scrollbar-thumb": {
    background: "#CBD5E1",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb:hover": { background: "#94A3B8" },
};

/** Pinned action bar, so the primary button is always reachable. */
export const payoutModalFooterSx = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: 1.5,
  px: { xs: 2.5, md: 3.5 },
  py: 2,
  borderTop: "1px solid #E2E8F0",
  backgroundColor: "#F8FAFC",
  flexShrink: 0,
  flexWrap: "wrap",
};

export const PayoutModalHeader = ({ title, onClose }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      px: { xs: 2.5, md: 3.5 },
      py: 2,
      bgcolor: ThemeColor.PURPLE,
      color: "#FFFFFF",
      flexShrink: 0,
    }}
  >
    <Typography
      fontFamily={FontFamily.Poppins}
      fontWeight={fontWeight.W600}
      fontSize={fontSize.h3.medium}
      color="#FFFFFF"
      // Double-clicking a heading to focus the dialog should not leave it
      // looking highlighted.
      sx={{ userSelect: "none" }}
    >
      {title}
    </Typography>
    <IconButton
      onClick={onClose}
      size="small"
      aria-label="Close"
      sx={{
        color: "#FFFFFF",
        bgcolor: "rgba(255, 255, 255, 0.15)",
        "&:hover": { bgcolor: "rgba(255, 255, 255, 0.3)" },
      }}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>
);

/** Field label used above every input in these modals. */
export const FieldLabel = ({ children }) => (
  <Typography
    className="mb-2"
    fontFamily={FontFamily.Lato}
    fontSize={fontSize.h4.large}
    fontWeight={fontWeight.W600}
    color="#334155"
  >
    {children}
  </Typography>
);
